import React, { useEffect, useState } from "react";
import { API_BASE } from "../../config/api";
import { authService } from "../../services/auth";
import { httpRequest } from "../../utils/httpClient";

interface VersionInfo {
  survey_version_id: string;
  version: number;
  published_at: string | null;
  name: string;
  slug: string;
}

interface AnalyticsData {
  versions: VersionInfo[];
  response_counts: Record<string, { count: number }>;
  persona_distribution: Record<string, number>;
  last_response_at: string | null;
  users_last_month: number;
  persona_profile_pct: number;
}

export default function SurveyAnalytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedSlug, setSelectedSlug] = useState("gamified");

  useEffect(() => {
    async function fetchAnalytics() {
      setLoading(true);
      setError("");
      try {
        const res = await httpRequest(
          `${API_BASE}/admin/surveys/analytics?survey_slug=${encodeURIComponent(selectedSlug)}`,
          {
            method: "GET",
            headers: authService.getAuthHeaders(),
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to load analytics");
        }
        const payload = await res.json();
        setData(payload);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    }
    fetchAnalytics();
  }, [selectedSlug]);

  const totalResponses = Object.values(data?.response_counts || {}).reduce(
    (sum, v) => sum + (v.count || 0),
    0
  );

  const handleExportCsv = () => {
    if (!data) return;
    const rows = [
      ["Survey Slug", "Version", "Version ID", "Published At", "Response Count"],
      ...data.versions.map((v) => [
        v.slug,
        String(v.version),
        v.survey_version_id,
        v.published_at || "",
        String(data.response_counts[v.survey_version_id]?.count || 0),
      ]),
    ];
    const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `survey-analytics-${selectedSlug}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Survey Analytics</h1>
        <div className="flex items-center gap-4">
          <select
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-pista-500"
          >
            <option value="">All Surveys</option>
            <option value="gamified">gamified</option>
            <option value="pista-wtp-pmf">pista-wtp-pmf</option>
          </select>
          <button
            onClick={handleExportCsv}
            disabled={!data || loading}
            className="px-4 py-2 bg-pista-700 text-white rounded-lg font-medium hover:bg-pista-800 disabled:bg-gray-300 transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading analytics...</div>
      ) : !data ? (
        <div className="text-center py-12 text-gray-500">No data available</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Total Responses</p>
              <p className="text-3xl font-bold text-gray-900">{totalResponses}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Versions</p>
              <p className="text-3xl font-bold text-gray-900">{data.versions.length}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Top Persona</p>
              <p className="text-xl font-bold text-gray-900 truncate">
                {Object.entries(data.persona_distribution).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Last Response Saved</p>
              <p className="text-xl font-bold text-gray-900">
                {data.last_response_at
                  ? new Date(data.last_response_at).toLocaleString()
                  : "—"}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Users (Last 30 Days)</p>
              <p className="text-3xl font-bold text-gray-900">
                {data.users_last_month ?? 0}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Personas Saved to Profile</p>
              <p className="text-3xl font-bold text-gray-900">
                {`${((data.persona_profile_pct ?? 0) * 100).toFixed(1)}%`}
              </p>
            </div>
          </div>

          {/* Versions Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Versions & Responses</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Version</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Published</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Responses</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.versions.map((v) => (
                    <tr key={v.survey_version_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{v.slug}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{v.version}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {v.published_at ? new Date(v.published_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {data.response_counts[v.survey_version_id]?.count || 0}
                      </td>
                    </tr>
                  ))}
                  {data.versions.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                        No versions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Persona Distribution */}
          {Object.keys(data.persona_distribution).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Persona Distribution</h2>
              <div className="space-y-4">
                {Object.entries(data.persona_distribution)
                  .sort((a, b) => b[1] - a[1])
                  .map(([persona, count]) => {
                    const pct = totalResponses > 0 ? (count / totalResponses) * 100 : 0;
                    return (
                      <div key={persona}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-800">{persona}</span>
                          <span className="text-gray-500">
                            {count} ({pct.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-pista-600 rounded-full transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
